import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import ModalShell from "@/components/ModalShell";

export default function MerchModal({ open, item, onClose, onSaved }) {
  const isEdit = Boolean(item);
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]); // new files (queue) — for both create and edit-add
  const [gallery, setGallery] = useState([]); // current image URLs on the item (edit mode)
  const [form, setForm] = useState({ name: "", description: "", price: "", active: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || "",
        description: item.description || "",
        price: String(item.price ?? ""),
        active: item.active !== false,
      });
      setGallery(item.image_urls && item.image_urls.length ? item.image_urls : (item.image_url ? [item.image_url] : []));
      setFiles([]);
    } else if (open) {
      setForm({ name: "", description: "", price: "", active: true });
      setFiles([]);
      setGallery([]);
    }
  }, [item, open]);

  if (!open) return null;

  function addFilesFromInput(e) {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles((prev) => [...prev, ...list]);
    if (fileRef.current) fileRef.current.value = "";
  }

  function removeQueuedFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function removeExistingImage(url) {
    try {
      const { data } = await api.delete(`/admin/merch/${item.id}/images`, { data: { image_url: url } });
      toast.success("Image removed");
      setGallery(data.image_urls || []);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (isEdit) {
        // Save text fields
        await api.patch(`/admin/merch/${item.id}`, {
          name: form.name.trim(),
          description: form.description,
          price: parseFloat(form.price || "0"),
          active: form.active,
        });
        // Upload any new queued images
        if (files.length) {
          const fd = new FormData();
          files.forEach((f) => fd.append("files", f));
          await api.post(`/admin/merch/${item.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Merch updated");
      } else {
        if (files.length === 0) {
          setBusy(false);
          return toast.error("Select at least one image");
        }
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("name", form.name.trim());
        fd.append("description", form.description);
        fd.append("price", form.price || "0");
        fd.append("active", form.active ? "true" : "false");
        await api.post("/admin/merch", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Merch created");
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  function resolveUrl(u) {
    if (!u) return "";
    return u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-xl" testId="merch-modal">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm"
        data-testid="merch-modal-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
        {isEdit ? "Edit Merch" : "New Merch"}
      </div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">
        {isEdit ? item.name : "Add Merchandise"}
      </h2>

      <form onSubmit={submit} className="mt-6 space-y-5">
        {/* Existing gallery (edit mode) */}
        {isEdit && gallery.length > 0 && (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 block">
              Current Images ({gallery.length})
            </label>
            <div className="grid grid-cols-4 gap-2" data-testid="merch-gallery">
              {gallery.map((url) => (
                <div key={url} className="relative aspect-square border border-border rounded-sm overflow-hidden group bg-muted">
                  <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(url)}
                    className="absolute top-1 right-1 p-1 rounded-sm text-white opacity-0 group-hover:opacity-100 transition"
                    style={{ background: "#E15C2C" }}
                    data-testid={`merch-remove-img`}
                    aria-label="Remove image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">
            {isEdit ? "Add More Images" : "Product Images"}
          </label>
          <div
            className="border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:bg-surface-elev transition"
            onClick={() => fileRef.current?.click()}
          >
            <UploadCloud className="h-6 w-6 mx-auto text-muted-foreground" />
            <div className="mt-2 text-sm">
              {files.length > 0
                ? `${files.length} image${files.length === 1 ? "" : "s"} ready to upload`
                : "Click to select one or more images"}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={addFilesFromInput}
              data-testid="merch-file"
            />
          </div>
          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2" data-testid="merch-queue">
              {files.map((f, i) => (
                <div key={i} className="relative aspect-square border border-border rounded-sm overflow-hidden bg-muted">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeQueuedFile(i)}
                    className="absolute top-1 right-1 p-1 rounded-sm bg-black/80 text-white"
                    aria-label="Remove from queue"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Field label="Name">
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="merch-name"
            className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
          />
        </Field>

        <Field label="Price (USD)">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            data-testid="merch-price"
            className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
            placeholder="0.00"
          />
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="merch-desc"
            className="w-full bg-transparent border border-border rounded-sm p-3 text-sm outline-none focus:border-foreground"
          />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="accent-foreground"
            data-testid="merch-active"
          />
          Active (visible to partners)
        </label>

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="merch-modal-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Save Changes" : "Create Merch"}
        </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">
        {label}
      </label>
      {children}
    </div>
  );
}
